import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { LoadingComponent } from '@app/admin/manage/modals/loading-component/loading.component';
import { AccessLevelTypes } from '@app/enums/AccessLevelTypes';
import { CreateAccessRequestCrcRequest } from '@app/generated-api';
import { AccessRequestService } from '@app/v2/services/access-request/access-request.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { AlertComponent } from '@components/atoms/alert/alert.component';
import {
    MdsButtonComponent,
    MdsCardActionsComponent,
    MdsCardComponent,
    MdsCardContentComponent,
    MdsTextFieldComponent,
    MdsTextareaComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { AuthService } from '@services/auth/auth.service';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { ModalSize, getModalConfig } from '@utility/modal-helper';
import { PersistentFormControl, PersistentFormGroup } from '@utility/persistent-forms';
import {
    maxLengthValidator,
    notWhitespaceValidator,
    phoneNumberFormatValidatorWithMaxLength,
} from '@utility/utility.validators';
import { filter, finalize, map, of, shareReplay, switchMap, take, tap } from 'rxjs';
import {
    DisplayErrorDialogComponent,
    DisplayErrorDialogData,
} from '../dialogs/display-error-dialog/display-error-dialog.component';
import { PageComponent } from '../page/page.component';
@Component({
    selector: 'medpace-create-access-request-crc',
    templateUrl: './create-access-request-crc.component.html',
    imports: [
        AlertComponent,
        MdsTextFieldComponent,
        MdsTextareaComponent,
        MdsButtonComponent,
        MdsCardComponent,
        MdsCardContentComponent,
        MdsCardActionsComponent,
        PageComponent,
        ReactiveFormsModule,
        LoadingComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAccessRequestCrcComponent {
    private readonly userService2 = inject(UserServiceV2);
    private readonly matDialog = inject(MatDialog);
    private readonly authService = inject(AuthService);
    private readonly route = inject(ActivatedRoute);
    private readonly snackBarService = inject(SnackbarService);
    private readonly accessRequestService2 = inject(AccessRequestService);
    private readonly destroyRef = inject(DestroyRef);
    formGroup = new PersistentFormGroup({
        emailAddress: new PersistentFormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            maxLengthValidator(50),
        ]),
        firstName: new PersistentFormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            maxLengthValidator(50),
        ]),
        lastName: new PersistentFormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            maxLengthValidator(50),
        ]),
        justification: new PersistentFormControl<string | null>(null, [
            notWhitespaceValidator(),
            maxLengthValidator(500),
        ]),
        phoneNumber: new PersistentFormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            phoneNumberFormatValidatorWithMaxLength(20),
        ]),
    });
    readonly isSuccess = signal(false);
    readonly errorMessageVisible = signal(false);
    readonly isLoading = signal(true);
    readonly isGuidValid = signal(true);

    readonly isAuthSignal = toSignal(this.authService.getIsAuthenticated());

    readonly userDataSignal = toSignal(
        this.authService.getIsAuthenticated().pipe(
            filter(Boolean),
            switchMap(() => this.userService2.hasAccount.get$()),
            filter(Boolean),
            switchMap(() => this.userService2.currentUserData.get$()),
            shareReplay(1)
        )
    );

    readonly title = computed(() => (this.isAuthSignal() ? 'Access request' : 'Register Account'));

    readonly hasPcsAccount = computed(() => !!this.userDataSignal());

    readonly hasActiveAccount = computed(() => this.userDataSignal()?.isActive ?? false);

    readonly isFormGroupVisible = computed(() => {
        const isAuth = this.isAuthSignal();
        const hasPcsAccount = this.hasPcsAccount();
        const hasActiveAccount = this.hasActiveAccount();
        const userData = this.userDataSignal();

        // Show form if: not authenticated, authenticated without PCS account, or authenticated but not active
        const isVisible = !isAuth || !hasPcsAccount || !hasActiveAccount;

        if (isVisible) {
            const emailHasValue = !!this.formGroup.controls.emailAddress.value;

            this.formGroup.enable();

            // Keep emailAddress disabled if it already has a value
            if (emailHasValue) {
                this.formGroup.controls.emailAddress.disable();
            }

            if (isAuth) {
                if (!hasPcsAccount) {
                    const claims = this.authService.claims;
                    this.formGroup.patchValue({
                        firstName: claims['given_name'],
                        lastName: claims['family_name'],
                        emailAddress: claims['email'],
                    });
                    this.formGroup.controls.emailAddress.disable();
                } else if (!hasActiveAccount && userData) {
                    this.formGroup.patchValue({
                        firstName: userData.firstName,
                        lastName: userData.lastName,
                        emailAddress: userData.emailAddress,
                        phoneNumber: userData.phoneNumber,
                    });
                    this.formGroup.controls.emailAddress.disable();
                }
            }
        } else {
            this.formGroup.disable();
        }

        return isVisible;
    });

    readonly guid = toSignal(this.route.queryParams.pipe(map((params) => params['guid'] ?? null)));

    readonly invitation$ = this.route.queryParams.pipe(
        map((params) => params['guid'] ?? null),
        switchMap((guid) => (guid ? this.accessRequestService2.getInvitation(guid) : of(null))),
        tap((invitation) => {
            this.isGuidValid.set(invitation ? true : false);
            if (!this.isAuthSignal() && invitation) {
                this.formGroup.controls.emailAddress.patchValue(invitation.userEmail);
                this.formGroup.controls.emailAddress.disable();
            }
        }),
        take(1),
        finalize(() => this.isLoading.set(false))
    );

    readonly invitation = toSignal(this.invitation$);
    readonly isValidAuthUserEmail = computed(() => {
        const invitation = this.invitation();
        const isAuth = this.isAuthSignal();

        if (!isAuth) return true; // If not authenticated, we don't need to validate the email

        const emailClaim = this.authService.claims['email'];

        if (!invitation || !emailClaim) return true;

        return invitation.userEmail.toLowerCase() === emailClaim.toLowerCase();
    });

    sendRequest() {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            return;
        }

        const guid = this.guid();
        if (!guid) return;

        this.accessRequestService2
            .createAccessRequestCrc(this.createObject(), guid)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                error: (e) => {
                    console.error('error', e);
                    this.matDialog.open<DisplayErrorDialogComponent, DisplayErrorDialogData>(
                        DisplayErrorDialogComponent,
                        {
                            ...getModalConfig(ModalSize.Small),
                            data: { description: e.error.ExceptionMessage },
                        }
                    );
                },
                complete: () => {
                    this.snackBarService.openInfoSnackbar('Request has been sent successfully.');
                    this.isSuccess.set(true);
                },
            });
    }

    createObject(): CreateAccessRequestCrcRequest {
        return this.hasPcsAccount() && this.hasActiveAccount()
            ? <CreateAccessRequestCrcRequest>{
                  userAccessGroupId: AccessLevelTypes.CRC,
                  userEmail: this.invitation()!.userEmail,
              }
            : <CreateAccessRequestCrcRequest>{
                  firstName: this.formGroup.controls.firstName.value,
                  lastName: this.formGroup.controls.lastName.value,
                  userEmail: this.formGroup.controls.emailAddress.value,
                  phoneNumber: this.formGroup.controls.phoneNumber.value,
                  justification: this.formGroup.controls.justification?.value,
                  userAccessGroupId: AccessLevelTypes.CRC,
              };
    }
}
